/**
 * 💬 TESTIMONIALS SECTION — COACHDIA LIGHT PREMIUM (Iter298)
 * ==========================================================
 *
 * - Mobile : carousel défilant (1 témoignage visible)
 * - Desktop : grille 3 cartes
 * - Bouton "Voir plus" → pagination /api/testimonials/public?page&limit
 * - CTA "Laisser un témoignage" → SubmitTestimonialModal (auth required)
 * - Modération obligatoire : seuls les status='approved' s'affichent
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Quote, MessageSquarePlus, Star, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import SubmitTestimonialModal from './SubmitTestimonialModal';
import { getBackendUrl } from '../config/environment';
import { safeFetchJSON } from '../utils/apiHelpers';

const fadeUp = { initial: { opacity: 0, y: 25 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const PAGE_SIZE = 6;

const initialsOf = (name) => {
  if (!name) return 'CD';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || 'CD';
};

const StarRating = ({ rating }) => (
  <div className="flex gap-0.5 mb-3" aria-label={`${rating} sur 5`}>
    {[1, 2, 3, 4, 5].map((s) => (
      <Star
        key={s}
        className="w-3.5 h-3.5"
        style={{
          fill: s <= rating ? '#D4AF37' : 'transparent',
          color: s <= rating ? '#D4AF37' : '#E5DFCE',
        }}
      />
    ))}
  </div>
);

const TestimonialCard = ({ t }) => (
  <motion.article
    {...fadeUp}
    transition={{ duration: 0.5 }}
    className="rounded-2xl p-6 sm:p-7 bg-white border border-[#EDE7D7] h-full flex flex-col"
    style={{ boxShadow: '0 4px 20px rgba(28,17,71,0.05)' }}
    data-testid="testimonial-card"
  >
    <Quote className="w-7 h-7 mb-4 text-[#D4AF37]" style={{ opacity: 0.5 }} />
    {t.rating ? <StarRating rating={t.rating} /> : null}
    <blockquote className="text-[#1C1147] leading-relaxed mb-5 flex-1 text-sm sm:text-base">
      « {t.message} »
    </blockquote>
    <div className="flex items-center gap-3 pt-4 border-t border-[#F0EDE6]">
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center text-[#1C1147] font-bold text-xs"
        style={{ background: 'linear-gradient(135deg, #FFE9A0, #D4AF37)' }}
        aria-hidden
      >
        {initialsOf(t.user_name)}
      </div>
      <div className="text-left min-w-0">
        <p className="text-[#1C1147] font-bold text-sm truncate">{t.user_name}</p>
        <p className="text-xs text-[#7A6F58] truncate">
          {t.role}
          {t.company ? ` · ${t.company}` : ''}
        </p>
      </div>
    </div>
  </motion.article>
);

const EmptyState = ({ onCTA }) => (
  <motion.div
    {...fadeUp}
    transition={{ duration: 0.5 }}
    className="rounded-3xl bg-white border-2 border-dashed border-[#D4AF37]/30 p-10 text-center"
    data-testid="testimonials-empty"
  >
    <Quote className="w-10 h-10 mx-auto mb-4 text-[#D4AF37]" style={{ opacity: 0.4 }} />
    <p className="font-serif text-xl text-[#1C1147] mb-2">Soyez le premier à témoigner.</p>
    <p className="text-sm text-[#5A5266] mb-6">
      Votre expérience inspire d'autres managers à franchir le pas.
    </p>
    <button
      type="button"
      onClick={onCTA}
      data-testid="empty-submit-testimonial-btn"
      className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm text-[#1C1147] transition-all hover:-translate-y-0.5"
      style={{
        background: 'linear-gradient(135deg, #D4AF37, #F3D060)',
        boxShadow: '0 6px 24px rgba(212,175,55,0.30)',
      }}
    >
      <MessageSquarePlus className="w-4 h-4" /> Partager votre expérience
    </button>
  </motion.div>
);

const TestimonialsSection = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [carouselIdx, setCarouselIdx] = useState(0);

  const fetchPage = useCallback(async (targetPage, append = false) => {
    const url = `${getBackendUrl()}/api/testimonials/public?page=${targetPage}&limit=${PAGE_SIZE}`;
    const data = await safeFetchJSON(url, { method: 'GET' });
    return data;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await fetchPage(1, false);
        if (cancelled) return;
        setItems(data.items || []);
        setTotal(data.total || 0);
        setHasMore(Boolean(data.has_more));
        setPage(1);
      } catch (e) {
        // No fallback content per product spec — keep empty state.
        if (!cancelled) {
          setItems([]);
          setTotal(0);
          setHasMore(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchPage]);

  const handleSeeMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const next = page + 1;
      const data = await fetchPage(next, true);
      setItems((prev) => [...prev, ...(data.items || [])]);
      setHasMore(Boolean(data.has_more));
      setPage(next);
    } catch (e) {
      toast.error('Impossible de charger plus de témoignages.');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleOpenModal = () => {
    if (!user) {
      toast.info('Connectez-vous pour partager votre expérience', {
        description: 'Vous serez redirigé vers la page de connexion',
      });
      setTimeout(() => navigate('/login'), 1200);
      return;
    }
    setIsModalOpen(true);
  };

  const carouselPrev = () => setCarouselIdx((i) => (i - 1 + items.length) % Math.max(items.length, 1));
  const carouselNext = () => setCarouselIdx((i) => (i + 1) % Math.max(items.length, 1));

  return (
    <section
      id="temoignages"
      className="px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto"
      data-testid="testimonials-section"
    >
      <motion.div {...fadeUp} transition={{ duration: 0.6 }} className="text-center mb-12">
        <p
          className="text-[10px] sm:text-[11px] tracking-[0.32em] uppercase font-semibold mb-4 text-[#B7901F]"
        >
          Preuve sociale
        </p>
        <h2 className="font-serif text-4xl sm:text-5xl text-[#1C1147] leading-tight tracking-tight">
          Ils incarnent la transformation.
        </h2>
        {total > 0 ? (
          <p className="text-sm text-[#5A5266] mt-3">
            {total} témoignage{total > 1 ? 's' : ''} validé{total > 1 ? 's' : ''}
          </p>
        ) : null}
      </motion.div>

      {loading ? (
        <div className="flex justify-center py-16" data-testid="testimonials-loading">
          <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState onCTA={handleOpenModal} />
      ) : (
        <>
          {/* Mobile carousel */}
          <div className="md:hidden" data-testid="testimonials-mobile-carousel">
            <div className="relative">
              <AnimatePresence mode="wait">
                <motion.div
                  key={carouselIdx}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <TestimonialCard t={items[carouselIdx]} />
                </motion.div>
              </AnimatePresence>
              {items.length > 1 ? (
                <div className="flex items-center justify-between mt-4">
                  <button
                    type="button"
                    onClick={carouselPrev}
                    aria-label="Témoignage précédent"
                    data-testid="carousel-prev-btn"
                    className="w-10 h-10 rounded-full bg-white border border-[#EDE7D7] flex items-center justify-center text-[#1C1147] hover:bg-[#FFF8E0]"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <div className="flex gap-1.5" aria-hidden>
                    {items.map((_, i) => (
                      <span
                        key={i}
                        className="w-1.5 h-1.5 rounded-full transition-all"
                        style={{
                          background: i === carouselIdx ? '#D4AF37' : '#E5DFCE',
                          width: i === carouselIdx ? '18px' : '6px',
                        }}
                      />
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={carouselNext}
                    aria-label="Témoignage suivant"
                    data-testid="carousel-next-btn"
                    className="w-10 h-10 rounded-full bg-white border border-[#EDE7D7] flex items-center justify-center text-[#1C1147] hover:bg-[#FFF8E0]"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          {/* Desktop grid */}
          <div className="hidden md:grid md:grid-cols-3 gap-5" data-testid="testimonials-desktop-grid">
            {items.map((t) => (
              <TestimonialCard key={t._id} t={t} />
            ))}
          </div>

          {hasMore ? (
            <div className="hidden md:flex justify-center mt-8">
              <button
                type="button"
                onClick={handleSeeMore}
                disabled={loadingMore}
                data-testid="testimonials-see-more-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border border-[#D4AF37] text-[#1C1147] font-semibold text-sm hover:bg-[#FFF8E0] transition-all disabled:opacity-60"
              >
                {loadingMore ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
                  </>
                ) : (
                  <>Voir plus de témoignages</>
                )}
              </button>
            </div>
          ) : null}
        </>
      )}

      {/* CTA "Laisser un témoignage" */}
      <motion.div
        {...fadeUp}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="flex flex-col items-center justify-center gap-3 mt-12"
      >
        <motion.button
          whileHover={{ scale: 1.04, y: -2 }}
          whileTap={{ scale: 0.97 }}
          onClick={handleOpenModal}
          data-testid="submit-testimonial-btn"
          className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-bold text-[#1C1147]"
          style={{
            background: 'linear-gradient(135deg, #D4AF37, #F3D060)',
            boxShadow: '0 6px 24px rgba(212,175,55,0.30)',
          }}
        >
          <MessageSquarePlus className="w-4 h-4" />
          Laisser un témoignage
        </motion.button>
        <p className="text-xs text-[#7A6F58]">
          Votre témoignage sera publié après modération.
        </p>
      </motion.div>

      <SubmitTestimonialModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </section>
  );
};

export default TestimonialsSection;
